"use client";

import { usePathname, useRouter } from "next/navigation";
import { FolderKanban, MessageSquare, Settings, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import type { AiProfile } from "@/types/ai";

export interface NavigationConfig {
  enabled: boolean;
  position: "bottom" | "top" | "left" | "right";
  style: "island" | "bar" | "minimal";
  showLabels: boolean;
  items: NavigationItem[];
  customIslands?: NavigationIsland[];
}

export interface NavigationItem {
  path: string;
  icon: LucideIcon;
  label: string;
  enabled?: boolean;
  badge?: string | number;
}

export interface NavigationIsland {
  id: string;
  position: "left" | "right" | "center";
  component: React.ReactNode;
  enabled: boolean;
}

interface Props {
  profile?: AiProfile | null;
  config?: Partial<NavigationConfig>;
  hideOnPaths?: string[];
  className?: string;
}

const DEFAULT_NAV_ITEMS: NavigationItem[] = [
  { path: "/builder", icon: FolderKanban, label: "Builder" },
  { path: "/chat", icon: MessageSquare, label: "Chat" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function PremiumNavigation({ 
  profile, 
  config,
  hideOnPaths = ["/onboarding", "/start", "/"],
  className = ""
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const { profile: themeProfile } = useTheme();
  
  // Use provided profile or theme profile
  const activeProfile = profile || themeProfile;
  
  // Check if navigation should be hidden
  if (hideOnPaths.some(path => pathname?.startsWith(path))) {
    return null;
  }
  
  // Merge config with profile config and defaults
  const profileNavConfig = activeProfile?.brand?.navigationConfig;
  const navConfig: NavigationConfig = {
    enabled: config?.enabled ?? profileNavConfig?.enabled ?? true,
    position: config?.position ?? profileNavConfig?.position ?? "bottom",
    style: config?.style ?? profileNavConfig?.style ?? "island",
    showLabels: config?.showLabels ?? profileNavConfig?.showLabels ?? true,
    items: config?.items ?? DEFAULT_NAV_ITEMS,
    customIslands: config?.customIslands ?? [],
  };
  
  if (!navConfig.enabled) {
    return null;
  }
  
  // Use profile theme colors or fallback
  const primaryColor = activeProfile?.brand?.primaryColor || "#6366f1";
  const secondaryColor = activeProfile?.brand?.secondaryColor || "#8b5cf6";
  const textColor = activeProfile?.brand?.textColor || "#ffffff";
  
  // Filter enabled items
  const enabledItems = navConfig.items.filter(item => item.enabled !== false);
  
  // Navigation container classes based on position
  const positionClasses = {
    bottom: "fixed bottom-0 left-0 right-0",
    top: "fixed top-0 left-0 right-0",
    left: "fixed left-0 top-0 bottom-0",
    right: "fixed right-0 top-0 bottom-0",
  };
  
  // Navigation flex direction based on position
  const flexDirection = {
    bottom: "flex-row",
    top: "flex-row",
    left: "flex-col",
    right: "flex-col",
  };
  
  const navContainerClass = positionClasses[navConfig.position] || positionClasses.bottom;
  const navFlexClass = flexDirection[navConfig.position] || flexDirection.bottom;
  
  // Render navigation based on style
  if (navConfig.style === "minimal") {
    return (
      <nav className={`${navContainerClass} z-50 ${className}`}>
        <div className="max-w-6xl mx-auto p-2">
          <div className={`flex ${navFlexClass} items-center justify-around gap-1`}>
            {enabledItems.map((item) => {
              const Icon = item.icon;
              const isActive = pathname?.startsWith(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => router.push(item.path)}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    isActive ? "bg-black/10" : "hover:bg-black/5"
                  }`}
                  style={{
                    color: isActive ? primaryColor : "rgba(0, 0, 0, 0.6)",
                    minWidth: '44px',
                    minHeight: '44px',
                  }}
                >
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    );
  }
  
  if (navConfig.style === "bar") {
    return (
      <nav 
        className={`${navContainerClass} z-50 p-2 md:p-4 pb-safe ${className}`}
        style={{
          background: `linear-gradient(to ${navConfig.position === "top" ? "bottom" : "top"}, ${primaryColor}E6 0%, ${primaryColor}CC 50%, ${secondaryColor}CC 100%)`,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          paddingBottom: navConfig.position === "bottom" ? 'calc(env(safe-area-inset-bottom) + 0.5rem)' : undefined,
          paddingTop: navConfig.position === "top" ? 'calc(env(safe-area-inset-top) + 0.5rem)' : undefined,
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className={`flex ${navFlexClass} items-center justify-around gap-1`}>
            {enabledItems.map((item) => {
              const Icon = item.icon;
              const isActive = pathname?.startsWith(item.path);
              const showLogo = (item.path === "/chat") && activeProfile?.brand?.logoUrl;
              
              return (
                <button
                  key={item.path}
                  onClick={() => router.push(item.path)}
                  className={`flex flex-col items-center gap-0.5 px-2 py-1.5 md:px-4 md:py-2.5 rounded-lg md:rounded-xl transition-all duration-200 relative group ${
                    isActive ? "scale-105" : "hover:scale-105"
                  }`}
                  style={{
                    color: isActive ? textColor : "rgba(255, 255, 255, 0.7)",
                    minWidth: '44px',
                    minHeight: '44px',
                  }}
                >
                  {isActive && (
                    <div 
                      className="absolute inset-0 rounded-lg md:rounded-xl"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        boxShadow: `0 4px 16px ${primaryColor}40`,
                      }}
                    />
                  )}
                  {showLogo && isActive ? (
                    <img
                      src={activeProfile.brand.logoUrl}
                      alt={activeProfile.name}
                      className="w-5 h-5 md:w-6 md:h-6 rounded relative z-10 object-cover"
                    />
                  ) : (
                    <Icon 
                      className={`w-4 h-4 md:w-5 md:h-5 relative z-10 transition-all duration-200 ${
                        isActive ? "text-white" : "group-hover:text-white"
                      }`}
                      strokeWidth={isActive ? 2.5 : 2}
                    />
                  )}
                  {navConfig.showLabels && (
                    <span 
                      className={`text-[10px] md:text-xs font-medium relative z-10 transition-all duration-200 ${
                        isActive ? "text-white font-semibold" : "group-hover:text-white/90"
                      }`}
                    >
                      {item.label}
                    </span>
                  )}
                  {item.badge && (
                    <span 
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-bold relative z-10"
                      style={{
                        background: secondaryColor,
                        color: textColor,
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                  {isActive && (
                    <div 
                      className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                      style={{ backgroundColor: secondaryColor }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    );
  }
  
  // Default: Island style
  return (
    <nav 
      className={`${navContainerClass} z-50 p-2 md:p-4 pb-safe ${className}`}
      style={{
        background: `linear-gradient(to ${navConfig.position === "top" ? "bottom" : "top"}, ${primaryColor}E6 0%, ${primaryColor}CC 50%, ${secondaryColor}CC 100%)`,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        paddingBottom: navConfig.position === "bottom" ? 'calc(env(safe-area-inset-bottom) + 0.5rem)' : undefined,
        paddingTop: navConfig.position === "top" ? 'calc(env(safe-area-inset-top) + 0.5rem)' : undefined,
      }}
    >
      <div className="max-w-6xl mx-auto">
        <div 
          className={`flex ${navFlexClass} items-center justify-around rounded-2xl p-1.5 md:p-2 shadow-xl border`}
          style={{
            background: `linear-gradient(135deg, ${primaryColor}20 0%, ${secondaryColor}20 100%)`,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderColor: `${primaryColor}30`,
            boxShadow: `0 4px 24px ${primaryColor}25, 0 1px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.12)`,
          }}
        >
          {/* Custom islands on left */}
          {navConfig.customIslands?.filter(island => island.position === "left" && island.enabled).map(island => (
            <div key={island.id} className="flex-shrink-0">
              {island.component}
            </div>
          ))}
          
          {/* Navigation items */}
          {enabledItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname?.startsWith(item.path);
            const showLogo = (item.path === "/chat") && activeProfile?.brand?.logoUrl;
            
            return (
              <button
                key={item.path}
                onClick={() => router.push(item.path)}
                className={`flex flex-col items-center gap-0.5 px-2.5 py-1.5 md:px-3 md:py-2 rounded-lg md:rounded-xl transition-all duration-200 relative group ${
                  isActive ? "scale-[1.02]" : "hover:scale-[1.02]"
                }`}
                style={{
                  color: isActive ? textColor : "rgba(255, 255, 255, 0.7)",
                  minWidth: '44px',
                  minHeight: '44px',
                }}
              >
                {isActive && (
                  <div 
                    className="absolute inset-0 rounded-lg md:rounded-xl"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      boxShadow: `0 4px 16px ${primaryColor}40`,
                    }}
                  />
                )}
                {showLogo && isActive ? (
                  <img
                    src={activeProfile.brand.logoUrl}
                    alt={activeProfile.name}
                    className="w-5 h-5 md:w-6 md:h-6 rounded relative z-10 object-cover"
                  />
                ) : (
                  <Icon 
                    className={`w-4 h-4 md:w-5 md:h-5 relative z-10 transition-all duration-200 ${
                      isActive ? "text-white" : "group-hover:text-white"
                    }`}
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                )}
                {navConfig.showLabels && (
                  <span 
                    className={`text-[10px] md:text-xs font-medium relative z-10 transition-all duration-200 leading-tight ${
                      isActive ? "text-white font-semibold" : "group-hover:text-white/90"
                    }`}
                  >
                    {item.label}
                  </span>
                )}
                {item.badge && (
                  <span 
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-bold relative z-10"
                    style={{
                      background: secondaryColor,
                      color: textColor,
                    }}
                  >
                    {item.badge}
                  </span>
                )}
                {isActive && (
                  <div 
                    className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ backgroundColor: secondaryColor }}
                  />
                )}
              </button>
            );
          })}
          
          {/* Custom islands on right */}
          {navConfig.customIslands?.filter(island => island.position === "right" && island.enabled).map(island => (
            <div key={island.id} className="flex-shrink-0">
              {island.component}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}
